import ArrowRight from '@img/icons/header/arrow-right.svg';
import noop from 'lodash/noop';
import * as React from 'react';
import { NavigationItemModel } from '../../Header.types';
import S from './DesktopNavigationDropdownLink.styles';

export type DesktopNavigationDropdownLinkProps = {
  item: NavigationItemModel;
  active?: boolean;
  visible?: boolean;
  dropdownLevel: string;
  onHover?: (id: string) => void;
  translations: Record<string, string>;
};

const DesktopNavigationDropdownLink: React.FC<DesktopNavigationDropdownLinkProps> = ({
  item,
  active,
  visible,
  dropdownLevel,
  onHover = noop,
  translations,
}) => {
  const hoverTimeoutRef = React.useRef<number | null>(null);

  const handleLinkFocus = React.useCallback((id) => {
    onHover(id);
  }, []);

  return (
    <S.NavItem data-visible={visible} onMouseEnter={() => handleMouseEnter(item.id)} onMouseLeave={handleMouseLeave}>
      <S.NavLink
        href={item.url}
        target={item.target ?? '_self'}
        aria-current={active}
        aria-label={item.text}
        data-focus-id={item.id}
        data-dropdown-level={dropdownLevel}
        data-last-dropdown={item.children === undefined}
        onKeyDown={handleKeyDown}
        onFocus={() => handleLinkFocus(item.id)}
        tabIndex={0}
      >
        <span data-new={item.isNew}>
          <span data-cta-text>{item.text}</span>
          {item.isNew && translations['custom.new'] && <S.New>{translations['custom.new']}</S.New>}
        </span>
        <ArrowRight />
      </S.NavLink>
    </S.NavItem>
  );

  /**
   * @description Sets a slight delay before showing the dropdown - prevent flickering when the user hovers over multiple navigation items quickly
   * @param id current navigation item id
   */
  function handleMouseEnter(id: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = (setTimeout(() => {
      onHover(id);
    }, 150) as unknown) as number; // delay before showing the dropdown
  }

  /**
   * @description Clear the timeout when the mouse leaves the navigation item - allows user to hover over another item momentarily without the children appearing
   */
  function handleMouseLeave(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }

  /**
   * @description Handles keyboard navigation for the dropdown links - allows the user to navigate to the sibling, child or parent item using the arrow keys
   */
  function handleKeyDown(event: React.KeyboardEvent<HTMLAnchorElement>): void {
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowLeft'
    ) {
      event.preventDefault();
      event.stopPropagation();
    }

    const focusItem = (id: string): void => {
      const focusedItem = document.querySelector<HTMLAnchorElement>(`[data-focus-id="${id}"]`);
      if (focusedItem) {
        focusedItem.focus();
      }
    };

    switch (event.key) {
      case 'ArrowRight':
        if (item.children) {
          // Navigate to first child item
          const childId = item.children[0]?.id;
          focusItem(childId);
        }
        break;
      case 'ArrowLeft':
        if (dropdownLevel !== '1') {
          // Navigate to parent item
          const parentId = item.id.split('/').slice(0, -1).join('/');
          focusItem(parentId);
        }
        break;
      case 'ArrowDown':
        if (item.id) {
          // Navigate to the next focusable element (mimic tab behavior)
          const siblingDownId =
            item.id.split('/').slice(0, -1).join('/') + '/' + (parseInt(item.id.split('/').slice(-1)[0], 10) + 1);
          focusItem(siblingDownId);
        }
        break;
      case 'ArrowUp':
        if (item.id) {
          // Navigate to the previous focusable element (mimic shift+tab behavior)
          const siblingUpId =
            item.id.split('/').slice(0, -1).join('/') + '/' + (parseInt(item.id.split('/').slice(-1)[0], 10) - 1);
          focusItem(siblingUpId);
        }
        break;
      default:
        break;
    }
  }
};

export default DesktopNavigationDropdownLink;
